/**
 * Sistema de validación de entrada para formularios
 * Previene ataques de inyección y valida datos de entrada
 */

import { sanitizeUserInput } from './sanitizer';

/**
 * Patrones de validación comunes
 */
const PATTERNS = {
  EMAIL: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
  PHONE: /^[\+]?[1-9][\d]{0,15}$/,
  PASSWORD: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
  ALPHANUMERIC: /^[a-zA-Z0-9\s]+$/,
  ALPHABETIC: /^[a-zA-Z\s]+$/,
  NUMERIC: /^\d+$/,
  DECIMAL: /^\d+(\.\d{1,2})?$/,
  URL: /^https?:\/\/[^\s/$.?#].[^\s]*$/i,
  DATE: /^\d{4}-\d{2}-\d{2}$/,
  TIME: /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/,
  CEDULA: /^[0-9]{6,12}$/,
  PASSPORT: /^[A-Z0-9]{6,12}$/i
};

/**
 * Mensajes de error en español
 */
const ERROR_MESSAGES = {
  REQUIRED: 'Este campo es obligatorio',
  EMAIL_INVALID: 'El formato del email no es válido',
  PHONE_INVALID: 'El formato del teléfono no es válido',
  PASSWORD_WEAK: 'La contraseña debe tener al menos 8 caracteres, incluir mayúsculas, minúsculas, números y símbolos',
  PASSWORD_MISMATCH: 'Las contraseñas no coinciden',
  MIN_LENGTH: (min) => `Debe tener al menos ${min} caracteres`,
  MAX_LENGTH: (max) => `No puede tener más de ${max} caracteres`,
  MIN_VALUE: (min) => `El valor mínimo es ${min}`,
  MAX_VALUE: (max) => `El valor máximo es ${max}`,
  INVALID_FORMAT: 'El formato no es válido',
  INVALID_DATE: 'La fecha no es válida',
  INVALID_TIME: 'La hora no es válida',
  INVALID_URL: 'La URL no es válida',
  INVALID_DOCUMENT: 'El número de documento no es válido',
  XSS_ATTEMPT: 'Se detectó contenido potencialmente malicioso'
};

/**
 * Clase principal de validación
 */
class FormValidator {
  constructor() {
    this.errors = {};
    this.sanitizedData = {};
  }

  /**
   * Valida un campo específico
   * @param {string} field - Nombre del campo
   * @param {any} value - Valor a validar
   * @param {Object} rules - Reglas de validación
   * @returns {boolean} - Si el campo es válido
   */
  validateField(field, value, rules = {}) {
    // Sanitizar entrada
    const sanitizedValue = sanitizeUserInput(value);
    this.sanitizedData[field] = sanitizedValue;

    // Verificar si es requerido
    if (rules.required && (!sanitizedValue || sanitizedValue.toString().trim() === '')) {
      this.errors[field] = ERROR_MESSAGES.REQUIRED;
      return false;
    }

    // Si no es requerido y está vacío, es válido
    if (!rules.required && (!sanitizedValue || sanitizedValue.toString().trim() === '')) {
      delete this.errors[field];
      return true;
    }

    // Validar longitud mínima
    if (rules.minLength && sanitizedValue.toString().length < rules.minLength) {
      this.errors[field] = ERROR_MESSAGES.MIN_LENGTH(rules.minLength);
      return false;
    }

    // Validar longitud máxima
    if (rules.maxLength && sanitizedValue.toString().length > rules.maxLength) {
      this.errors[field] = ERROR_MESSAGES.MAX_LENGTH(rules.maxLength);
      return false;
    }

    // Validar valor mínimo
    if (rules.minValue !== undefined && Number(sanitizedValue) < rules.minValue) {
      this.errors[field] = ERROR_MESSAGES.MIN_VALUE(rules.minValue);
      return false;
    }

    // Validar valor máximo
    if (rules.maxValue !== undefined && Number(sanitizedValue) > rules.maxValue) {
      this.errors[field] = ERROR_MESSAGES.MAX_VALUE(rules.maxValue);
      return false;
    }

    // Validar patrón
    if (rules.pattern && !rules.pattern.test(sanitizedValue)) {
      this.errors[field] = rules.customMessage || ERROR_MESSAGES.INVALID_FORMAT;
      return false;
    }

    // Validaciones específicas por tipo
    if (rules.type) {
      switch (rules.type) {
        case 'email':
          if (!PATTERNS.EMAIL.test(sanitizedValue)) {
            this.errors[field] = ERROR_MESSAGES.EMAIL_INVALID;
            return false;
          }
          break;

        case 'phone':
          if (!PATTERNS.PHONE.test(sanitizedValue.replace(/\s/g, ''))) {
            this.errors[field] = ERROR_MESSAGES.PHONE_INVALID;
            return false;
          }
          break;

        case 'password':
          if (!PATTERNS.PASSWORD.test(sanitizedValue)) {
            this.errors[field] = ERROR_MESSAGES.PASSWORD_WEAK;
            return false;
          }
          break;

        case 'url':
          if (!PATTERNS.URL.test(sanitizedValue)) {
            this.errors[field] = ERROR_MESSAGES.INVALID_URL;
            return false;
          }
          break;

        case 'date':
          if (!PATTERNS.DATE.test(sanitizedValue) || isNaN(Date.parse(sanitizedValue))) {
            this.errors[field] = ERROR_MESSAGES.INVALID_DATE;
            return false;
          }
          break;

        case 'time':
          if (!PATTERNS.TIME.test(sanitizedValue)) {
            this.errors[field] = ERROR_MESSAGES.INVALID_TIME;
            return false;
          }
          break;

        case 'cedula':
          if (!PATTERNS.CEDULA.test(sanitizedValue.replace(/\D/g, ''))) {
            this.errors[field] = ERROR_MESSAGES.INVALID_DOCUMENT;
            return false;
          }
          break;

        case 'passport':
          if (!PATTERNS.PASSPORT.test(sanitizedValue)) {
            this.errors[field] = ERROR_MESSAGES.INVALID_DOCUMENT;
            return false;
          }
          break;
      }
    }

    // Validación personalizada
    if (rules.custom && typeof rules.custom === 'function') {
      const customResult = rules.custom(sanitizedValue, this.sanitizedData);
      if (customResult !== true) {
        this.errors[field] = customResult || ERROR_MESSAGES.INVALID_FORMAT;
        return false;
      }
    }

    // Si llegamos aquí, el campo es válido
    delete this.errors[field];
    return true;
  }

  /**
   * Valida un formulario completo
   * @param {Object} data - Datos del formulario
   * @param {Object} schema - Esquema de validación
   * @returns {boolean} - Si el formulario es válido
   */
  validateForm(data, schema) {
    this.errors = {};
    this.sanitizedData = {};
    let isValid = true;

    for (const [field, rules] of Object.entries(schema)) {
      const fieldValid = this.validateField(field, data[field], rules);
      if (!fieldValid) {
        isValid = false;
      }
    }

    return isValid;
  }

  /**
   * Valida contraseñas coincidentes
   * @param {string} password - Contraseña
   * @param {string} confirmPassword - Confirmación de contraseña
   * @param {string} fieldName - Nombre del campo de confirmación
   * @returns {boolean} - Si las contraseñas coinciden
   */
  validatePasswordMatch(password, confirmPassword, fieldName = 'confirmPassword') {
    if (password !== confirmPassword) {
      this.errors[fieldName] = ERROR_MESSAGES.PASSWORD_MISMATCH;
      return false;
    }
    delete this.errors[fieldName];
    return true;
  }

  /**
   * Obtiene los errores de validación
   * @returns {Object} - Objeto con errores por campo
   */
  getErrors() {
    return { ...this.errors };
  }

  /**
   * Obtiene los datos sanitizados
   * @returns {Object} - Datos sanitizados
   */
  getSanitizedData() {
    return { ...this.sanitizedData };
  }

  /**
   * Limpia los errores
   */
  clearErrors() {
    this.errors = {};
  }

  /**
   * Verifica si hay errores
   * @returns {boolean} - Si hay errores
   */
  hasErrors() {
    return Object.keys(this.errors).length > 0;
  }
}

/**
 * Esquemas de validación predefinidos
 */
export const VALIDATION_SCHEMAS = {
  // ✅ Esquema para LOGIN (NO valida fortaleza de contraseña)
  LOGIN: {
    email: { required: true, type: 'email' },
    password: { required: true } // Solo requiere que exista, NO valida fortaleza
  },

  // ✅ Esquema para CREAR/EDITAR USUARIOS (SÍ valida fortaleza)
  USER: {
    name: { required: true, minLength: 2, maxLength: 50, type: 'alphabetic' },
    lastName: { required: true, minLength: 2, maxLength: 50, type: 'alphabetic' },
    email: { required: true, type: 'email' },
    phone: { required: true, type: 'phone' },
    role: { required: true, pattern: /^(admin|user|manager)$/ },
    password: { required: true, type: 'password' } // Valida fortaleza completa
  },

  RESERVATION: {
    client_name: { required: true, minLength: 2, maxLength: 100 },
    client_email: { required: true, type: 'email' },
    client_phone: { required: true, type: 'phone' },
    total_amount: { required: true, minValue: 0, pattern: PATTERNS.DECIMAL },
    departure_date: { required: true, type: 'date' },
    return_date: { type: 'date' }
  },

  OFFICE: {
    name: { required: true, minLength: 2, maxLength: 100 },
    address: { required: true, minLength: 10, maxLength: 200 },
    phone: { required: true, type: 'phone' },
    email: { required: true, type: 'email' }
  }
};

// Instancia singleton del validador
const validator = new FormValidator();

export default validator;
export { PATTERNS, ERROR_MESSAGES };